// components/Recordings/RecordingPlayerPage.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Calendar, Clock, Play, Tag, FileText, Download, Eye, CheckCircle2 } from "lucide-react";
import { BackButton, AppButton } from "../ui";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import RecordingListItem from "./RecordingListItem";
import VideoComments from "../Videos/VideoComments";
import ProtectedVideoPlayer from "../Common/ProtectedVideoPlayer";
import CommonToaster from "../Common/CommonToaster";
import { startSession } from "../../utils/sessionTracker";
import useVideoWatchTracker from "../../hooks/useVideoWatchTracker";
import { usePlan } from "../../contexts/PlanContext";
import { getCurrentUser } from "../../utils/auth";
import { isDesktopApp } from "../../utils/desktopApp";
import DesktopAppRequired from "../Common/DesktopAppRequired";
import useMaterialViewer, { isViewableType } from "../Materials/useMaterialViewer";
import { canDownloadFile } from "../ContentProtection/contentAccess";
import "./Recordings.scss";

// ─── Inline Video Player ───
// The player brings the watermark, the protection hook and its own controls;
// this adds the watch-time tracking and the resume point.
const RecordingVideo = ({ url, loading, showVideo, currentRec, onUrlExpired, baselineSec, resumeSec, onProgress }) => {
  const videoRef = useRef(null);

  // Real watch-time tracking → feeds the progress rings in the recordings lists.
  useVideoWatchTracker({
    videoRef,
    videoId: currentRec?._id,
    durationMin: currentRec?.duration || 0,
    baselineSec,
    active: !!url && showVideo,
    onProgress,
  });

  return (
    <div className="player-video">
      <div className="player-video__container">
        {loading || !showVideo ? (
          <Shimmer height="100%" />
        ) : url ? (
          // Continue watching: the player seeks once to the stored playhead for
          // this recording; a refreshed link (onUrlExpired) resumes in place.
          <ProtectedVideoPlayer
            ref={videoRef}
            src={url}
            title={currentRec?.topic || currentRec?.title}
            preload="auto"
            startAt={resumeSec}
            mediaKey={currentRec?._id}
            durationHint={currentRec?.duration > 0 ? currentRec.duration * 60 : 0}
            onExpired={onUrlExpired}
          />
        ) : (
          <div className="player-video__unavailable">
            <div className="player-video__unavailable-icon">
              <Play />
            </div>
            <h5>Video unavailable</h5>
            <p>This video cannot be played right now</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Helpers ───
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "Asia/Kolkata",
      })
    : "";

// ─── Main Page ───
const RecordingPlayerPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPlanFeature } = usePlan();
  const currentUser = getCurrentUser();

  const [recordings, setRecordings] = useState([]);
  const [currentRec, setCurrentRec] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [sidebarLoading, setSidebarLoading] = useState(true);
  const [showVideo, setShowVideo] = useState(false);
  const [linkedMaterials, setLinkedMaterials] = useState([]);
  const { openMaterial, downloadMaterial, viewer: materialViewer } = useMaterialViewer();
  const [viewStatus, setViewStatus] = useState({}); // { videoId: { viewed, viewCount } }
  const [markingViewed, setMarkingViewed] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [courseVideoSecurity, setCourseVideoSecurity] = useState(false);

  // Desktop app required only when plan+institute enabled AND course has videoSecurity on
  const requireDesktopApp = hasPlanFeature('videoSecurityEnabled') && courseVideoSecurity && !isDesktopApp() && currentUser?.usertype === 'student';

  // ─── Session time tracking (one call on unmount / nav-away) ───
  useEffect(() => {
    if (!currentRec) return;
    const tracker = startSession({
      activityType: 'video',
      referenceId: currentRec._id,
      referenceTitle: currentRec.topic || currentRec.title || 'Recording',
    });
    return () => tracker.end();
  }, [currentRec?._id]);

  // Fetch sidebar list + view status in parallel
  useEffect(() => {
    (async () => {
      setSidebarLoading(true);
      try {
        const { data } = await apiClient.get("/videos/student/my-videos");
        const vids = data?.data || [];
        setRecordings(vids);
        if (data?.videoSecurity) setCourseVideoSecurity(true);

        // Fetch view status for the batch (single API call)
        const batchId = vids[0]?.batch?._id || vids[0]?.batch;
        if (batchId) {
          try {
            const vsRes = await apiClient.get(`/videos/batch/${batchId}/view-status`);
            setViewStatus(vsRes.data?.data || {});
          } catch {}
        }
      } catch {
        setRecordings([]);
      }
      setSidebarLoading(false);
    })();
  }, []);

  // Resolve current rec from list
  useEffect(() => {
    if (recordings.length) {
      setCurrentRec(recordings.find((r) => r._id === id) || null);
    }
  }, [recordings, id]);

  // Keep local view-status in sync with persisted watch progress so the
  // "Viewed" badge / view count reflect the latest without a refetch.
  const handleWatchProgress = useCallback((watchDuration, totalSec, lastPosition) => {
    const vid = currentRec?._id;
    if (!vid) return;
    const percent = totalSec > 0
      ? Math.min(100, Math.round((watchDuration / totalSec) * 100))
      : (watchDuration > 0 ? 100 : 0);
    setViewStatus((prev) => ({
      ...prev,
      [vid]: { ...(prev[vid] || {}), viewed: true, watchDuration, lastPosition, percent },
    }));
  }, [currentRec?._id]);

  // A fresh presigned link for a stream that expired mid-watch. The player
  // stays mounted and puts the playhead back where it was on the new link; a
  // failed fetch keeps the old one, and the player offers "Try again".
  const fetchStreamUrl = useCallback(async () => {
    if (!currentRec) return;
    try {
      const { data } = await apiClient.get(`/videos/${currentRec._id}/stream`);
      if (data?.data?.url) setVideoUrl(data.data.url);
    } catch { /* see above */ }
  }, [currentRec]);

  useEffect(() => {
    if (!currentRec) return;
    let alive = true;
    setLoading(true);
    setVideoUrl("");
    setShowVideo(false);

    (async () => {
      try {
        const { data } = await apiClient.get(`/videos/${currentRec._id}/stream`);
        if (alive) {
          setVideoUrl(data?.data?.url || "");
          setLoading(false);
          setTimeout(() => alive && setShowVideo(true), 1500);
        }
      } catch {
        if (alive) {
          setVideoUrl("");
          setLoading(false);
          setTimeout(() => alive && setShowVideo(true), 1500);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [currentRec]);

  // Fetch all materials for current video (video-level + linked batch materials)
  useEffect(() => {
    if (!currentRec?._id) {
      setLinkedMaterials([]);
      return;
    }
    let alive = true;
    const batchId = currentRec.batch?._id || currentRec.batch;
    (async () => {
      try {
        // Fetch video-level materials (uploaded directly)
        const videoMatsPromise = apiClient.get(`/materials/video/${currentRec._id}`).catch(() => ({ data: { data: [] } }));
        // Fetch linked batch materials
        const linkedMatsPromise = batchId
          ? apiClient.get(`/materials/batch/${batchId}/by-video/${currentRec._id}`).catch(() => ({ data: { data: [] } }))
          : Promise.resolve({ data: { data: [] } });

        const [videoRes, linkedRes] = await Promise.all([videoMatsPromise, linkedMatsPromise]);
        if (!alive) return;

        const videoMats = (videoRes.data?.data || []).filter(m => m.status === "ready");
        const linkedMats = linkedRes.data?.data || [];
        // Deduplicate by _id
        const seen = new Set(videoMats.map(m => String(m._id)));
        const merged = [...videoMats, ...linkedMats.filter(m => !seen.has(String(m._id)))];
        setLinkedMaterials(merged);
      } catch {
        if (alive) setLinkedMaterials([]);
      }
    })();
    return () => { alive = false; };
  }, [currentRec]);

  // ─── Render ───
  return (
    <>
    <div className="recordings-page">
      {/* Back link */}
      <BackButton onClick={() => navigate('/s/learning?tab=classes')} />

      <div className="player-page">
        {/* ── Main column ── */}
        <div className="player-page__main">
          {/* Player */}
          {requireDesktopApp ? (
            <DesktopAppRequired />
          ) : (
            <RecordingVideo
              url={videoUrl}
              loading={loading}
              showVideo={showVideo}
              currentRec={currentRec}
              onUrlExpired={fetchStreamUrl}
              baselineSec={viewStatus[currentRec?._id]?.watchDuration || 0}
              resumeSec={
                viewStatus[currentRec?._id]?.lastPosition
                ?? viewStatus[currentRec?._id]?.watchDuration
                ?? 0
              }
              onProgress={handleWatchProgress}
            />
          )}

          {/* Info + Materials */}
          {currentRec && (
            <div className="player-info">
              <h3 className="player-info__title">
                {currentRec.topic || currentRec.title || "Recording"}
              </h3>

              <div className="player-info__meta">
                <span>
                  <Calendar size={14} />
                  {formatDate(currentRec.recordedAt)}
                </span>
                {currentRec.duration && (
                  <span>
                    <Clock size={14} />
                    {currentRec.duration}
                  </span>
                )}
                <span>
                  <Eye size={14} />
                  {viewStatus[currentRec._id]?.viewCount ?? currentRec.viewCount ?? 0} views
                </span>
                {currentRec.fileType && (
                  <span className="player-info__topic-badge">
                    {currentRec.fileType}
                  </span>
                )}
              </div>

              {/* Mark as Viewed button */}
              <div className="player-info__actions" style={{ marginTop: 8 }}>
                {viewStatus[currentRec._id]?.viewed ? (
                  <span className="player-info__viewed-badge">
                    <CheckCircle2 size={14} /> Viewed
                  </span>
                ) : (
                  <AppButton
                    variant="outline"
                    size="sm"
                    disabled={markingViewed}
                    onClick={async () => {
                      setMarkingViewed(true);
                      try {
                        await apiClient.post(`/videos/${currentRec._id}/mark-viewed`);
                        setViewStatus((prev) => ({
                          ...prev,
                          [currentRec._id]: {
                            viewed: true,
                            viewCount: (prev[currentRec._id]?.viewCount || 0) + 1,
                          },
                        }));
                        setToast({ show: true, message: 'Marked as viewed!', type: 'success' });
                      } catch (err) {
                        console.error('Mark as viewed failed:', err);
                        setToast({ show: true, message: err?.response?.data?.message || 'Failed to mark as viewed.', type: 'error' });
                      }
                      setMarkingViewed(false);
                    }}
                  >
                    <Eye size={14} /> Mark as Viewed
                  </AppButton>
                )}
              </div>

              {currentRec.topic && currentRec.title && currentRec.topic !== currentRec.title && (
                <div className="player-info__topic-badge">
                  <Tag size={12} />
                  {currentRec.topic}
                </div>
              )}

              {currentRec.description && (
                <p className="player-info__description">{currentRec.description}</p>
              )}

              {linkedMaterials.length > 0 && (
                <div className="player-info__materials">
                  {linkedMaterials.map((mat) => {
                    // In-app viewer, so the file's content protection applies;
                    // only a downloadable, non-viewable type downloads.
                    const inApp = isViewableType(mat.fileType) || !canDownloadFile(mat);
                    return (
                      <button
                        key={mat._id}
                        className="player-info__material-chip"
                        onClick={() => (inApp ? openMaterial(mat) : downloadMaterial(mat))}
                      >
                        <FileText size={13} />
                        <span>{mat.title}</span>
                        {mat.fileType && <span className="player-info__material-type">{mat.fileType}</span>}
                        {inApp ? <Eye size={11} /> : <Download size={11} />}
                      </button>
                    );
                  })}
                </div>
              )}
              {materialViewer}
            </div>
          )}

          {/* Comments */}
          {currentRec && (
            <div className="player-comments">
              <VideoComments videoId={currentRec._id} role="student" />
            </div>
          )}
        </div>

        {/* ── Sidebar ── */}
        <div className="player-page__sidebar">
          <div className="player-sidebar">
            <div className="player-sidebar__header">
              <h5>All Recordings</h5>
              {recordings.length > 0 && <span>{recordings.length}</span>}
            </div>

            <div className="player-sidebar__list">
              {sidebarLoading ? (
                <Shimmer height={68} count={4} />
              ) : (
                recordings.map((rec) => (
                  <RecordingListItem
                    key={rec._id}
                    rec={{
                      ...rec,
                      date: rec.recordedAt,
                      thumbnail: rec.thumbnailUrl || null,
                    }}
                    active={rec._id === id}
                    viewInfo={viewStatus[rec._id]}
                    onClick={() => navigate(`/s/recordings/${rec._id}`)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    <CommonToaster
      show={toast.show}
      message={toast.message}
      type={toast.type}
      onClose={() => setToast(t => ({ ...t, show: false }))}
    />
    </>
  );
};

export default RecordingPlayerPage;
